import * as React from 'react';
import "./FetchElement.scss";
import { useEffect, useMemo, useState } from 'react';
import { FetchProps, FetchDefines } from './IFetch';
import { Props } from '../../common/types/props';
import { Defines } from '../../common/types/defines';
import { FormBuilder } from '../../common/FormBuilder';
import { ColorDefines, ElementColorDefines } from '../../interface/IColors';

interface FetchElementProps {
  props: FetchProps;
  defines: FetchDefines;
  colors: ColorDefines;
}

interface ChildrenElementProps {
  props: Props;
  defines: Defines;
  colors: ColorDefines;
}

const FetchElement = ({ props, defines, colors }: FetchElementProps) => {
  const [children, setChildren] = useState<ChildrenElementProps[]>();

  const params = useMemo(() => {
    if (!defines.params) return {};
    // console.log(defines, props)
    return defines.params.reduce((p, param) => {
      if (typeof param === "string") {
        p[param] = (props.values as {[key: string]: any})[param];
      } else if (typeof param === "object" && !Array.isArray(param)) {
        p = Object.assign(p, param);
      } else if (typeof param === "function") {
        try {
          p = param(p);
        } catch (e) {
          try {
            p = param(p.value)
          } catch (e) {
            p = e
          }
        }
      }
      return p;
    }, {} as { [key: string]: string });
  }, [defines.params, JSON.stringify(props.values)]);

  const url = useMemo(() => {
    if (!defines.url) return null;

    const urlParams = defines.url.match(/{(.*?)}/g).map(v => v.slice(1, -1));
    if (urlParams.some(u => (!Object.keys(params).includes(u)) || params[u] == null)) {
      // console.log(`some param is null.`, defines.url, params);
      return null;
    }

    try {
      const url = defines.url.replace(/({.*?})/g, (w, $1) => {
        const key = $1.match(/{(.+?)}/)[1];
        const p = params[key];
        if (!p) throw new Error(`params is null. (${key})\n${JSON.stringify(params, null, 2)}`);
        return params[key] || '';
      });
      return url;
    } catch (e) {
      console.error(e);
      return undefined;
    }
    // console.log("# url", _url, params, props)
  }, [
    defines.url,
    JSON.stringify(props.values)
  ]);

  const method = useMemo(() => defines.option?.method || "GET", [defines.option?.method]);
  const option = useMemo(() => {
    return defines.option ?
      ({
        method: method,
        headers: method == "POST" ? {
          "Content-Type": "application/json"
        } : undefined,
        body: method == "POST" ?
          JSON.stringify(defines.option.body || {}):
          undefined
      }) : undefined
  }, [defines.option, method, JSON.stringify(defines.option?.body || {})]);
  
  useEffect(() => {
    if (!url) {
      setChildren(defines.def ?
        [{
          defines: Object.assign(defines.def, { name: defines.name }),
          props: {
            visible: props.visible,
            values: props.values,
            parentValues: (props.values as any)[defines.name],
            dispatchValid: props.dispatchValid,
            getData: props.getData
          },
          colors
        }] : []);
      return;
    }

    fetch(url, option)
      .then(res => {
        setChildren([]);
        return res.json();
      })
      .then((res: Defines[]) => {
        // console.log(defines.name + " response", res);
        const isError = (res as any).error;
        if (isError) {
          // console.error(res);
        }
        const raw = (isError ?
          [] :
          (Array.isArray(res) ? res : [res]));
        const data = defines.callback ? defines.callback(raw, props, defines) : raw;
        // console.log(res)
        setChildren(data.map(d => {
          const name = d.name ?
            d.name.replace(/[_]([a-z])/g, (w: any, $1: string) => $1.toUpperCase()):
            null;
          const childDefines = name ? Object.assign(d, { name }) : d;
          return ({
            defines: childDefines,
            props: {
              visible: props.visible,
              values: props.values,
              parentValues: (props.values as any)[defines.name],
              dispatchValid: props.dispatchValid,
              getData: props.getData
            },
            colors
          });
        }));
      })
      .catch(err => {
        console.error(err);
        setChildren(defines.def ?
          [{
            defines: Object.assign(defines.def, { name: defines.name }),
            props: {
              visible: props.visible,
              values: props.values,
              parentValues: (props.values as any)[defines.name],
              dispatchValid: props.dispatchValid,
              getData: props.getData
            },
            colors
          }] : []);
      });
  }, [url, option, defines.callback]);
  
  const [fetchedElements, setFetchedElements] = useState<JSX.Element[]>();
  useEffect(() => {
    const def = ([
      <div key="progress" className="fetch">
        <div className="loader">
          <div className="duo duo1">
            <div className="dot dot-a"></div>
            <div className="dot dot-b"></div>
          </div>
          <div className="duo duo2">
            <div className="dot dot-a"></div>
            <div className="dot dot-b"></div>
          </div>
        </div>
      </div>
    ]);
    const ele = children && children.length > 0 ? FormBuilder(children, defines.name) : def;
    setFetchedElements(ele);
  }, [children, defines.def]);
  
  return (<>{fetchedElements}</>);
}

export default FetchElement;